const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const usdPrecise = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

/**
 * null means "not reported / not set" and must never render as $0.00 — a null limit
 * means unlimited while 0 blocks every request. Callers pass the dash text.
 */
export function money(value: number | null, nullText = 'not set'): string {
  if (value === null || value === undefined) return nullText;
  if (!Number.isFinite(value)) return nullText;
  // Sub-cent spend is normal on an LLM gateway; rounding it to $0.00 reads as "no spend".
  if (value !== 0 && Math.abs(value) < 0.01) return usdPrecise.format(value);
  return usd.format(value);
}

export function moneyOrZero(value: number | null): string {
  return money(value, money(0));
}

export function utcTimestamp(iso: string | null): string {
  if (!iso) return 'unknown';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'unknown';
  return `${d.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
}

/** "HH:MM UTC" for as-of labels. */
export function utcTime(iso: string | null): string {
  if (!iso) return 'unknown';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'unknown';
  return `${d.toISOString().slice(11, 16)} UTC`;
}

export function utcDate(iso: string | null): string {
  if (!iso) return 'none';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'invalid';
  return d.toISOString().slice(0, 10);
}

export function daysUntil(iso: string | null, now: Date = new Date()): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((d.getTime() - now.getTime()) / 86_400_000);
}

export function relativeDays(days: number): string {
  if (days === 0) return 'today';
  if (days === 1) return 'in 1 day';
  if (days === -1) return '1 day ago';
  return days > 0 ? `in ${days} days` : `${-days} days ago`;
}

/** Percent of limit consumed, or null when there is no usable limit to divide by. */
export function limitPct(used: number | null, limit: number | null): number | null {
  if (limit === null || limit <= 0 || used === null) return null;
  return Math.min(100, Math.max(0, (used / limit) * 100));
}
